import type { ReactNode } from 'react';

// The stacked-group contract, imported rather than re-declared.
//
// `TextInputPosition` belongs to the GROUP, not to `TextInput` — `FieldGroup` is what assigns it,
// and it assigns it to any child that accepts one. Re-declaring a structurally identical union here
// would let the two drift into disagreeing about what a position is while every type still checked,
// which is precisely the failure a group of mixed field types would surface at runtime.
// `TextArea/types.ts`, `Select/types.ts` and `DatePicker/types.ts` take the same import for the
// same reason.
import type { TextInputPosition } from '../TextInput/types';

/**
 * The one short format this component owns, for both parsing and display.
 *
 * ── Why the component owns a format at all ────────────────────────────────────────────────────
 *
 * Nothing under the field supplies one. The slot list is generated here, the typed entry is parsed
 * here, and the two must agree exactly or a user could type a string the list itself would never
 * offer. So the format is a decision this file makes out loud, and it is a SINGLE constant used by
 * `slots.ts` to build every label and by `TimePicker.tsx` to parse every typed entry —
 * `DATE_PICKER_FORMAT` is the same idea for the same reason.
 *
 * ── Why 12-hour, and why it is not derived from the locale ────────────────────────────────────
 *
 * 12-hour because that is what the field being replaced already shows. The legacy
 * `common/components/Inputs/TimePicker` renders `hh:mm a` and its slot list reads `09:00AM`, so
 * every host that has ever set a time in this product has read it that way. 24-hour `HH:mm` is
 * arguably the better fit for a British product — it is the same argument that pinned the date
 * field to day-first — but unlike day-first it is not a correctness argument: `09:30 AM` is not
 * ambiguous, it is merely longer. Changing it would change what every existing host reads the
 * moment the refresh flag flips, which would make a visual refresh a behavioural one.
 *
 * It is deliberately not read from `navigator.language` or from a locale provider, for the reason
 * spelled out beside `DATE_PICKER_FORMAT`: a parse whose meaning depends on the reader's machine
 * cannot be tested, cannot be supported, and silently changes what a shared form means depending on
 * who opens it. Localising it — and offering 24-hour — is a real follow-up, and it needs the field
 * to show its active format, which is a design question this component has no drawing to answer
 * from (see the KTD7 note in `TimePicker.tsx`).
 *
 * Two visible differences from the legacy list. This field writes `9:30 AM` where the legacy slot
 * rows read `09:30AM`: a space before the meridiem, and no leading zero on the hour. The zero was
 * dropped on 2026-10-01 after review ("05:15 PM sucks"): the hint beneath the field already reads
 * `5:15 PM (EDT)`, and a padded hour made the same time look different twice on one screen. `h:mm a`
 * is the format `date-fns` can both print and parse without a second spelling, and it still parses
 * a typed `09:30AM` or `05:15 PM`, because `h` accepts one or two digits. The legacy component
 * reached its spaceless form by concatenating strings by hand (`utils.ts`,
 * `getNearestTimeOptionLabel`) rather than by choosing it. See the entry guard in `TimePicker.tsx`.
 *
 * Exported because a caller writing a test, or writing help text beside the field, needs to be able
 * to name the same format rather than re-typing the string.
 */
export const TIME_PICKER_FORMAT = 'h:mm a';

/**
 * The format spelled the way a person reads it.
 *
 * Shown only while the field is FOCUSED, not whenever it is empty, exactly as
 * `DATE_PICKER_PLACEHOLDER` is: an empty resting field is a 14px floating label and nothing else,
 * and the hint arrives at the moment it is useful. The attribute is always present;
 * `timePickerControlRecipe` paints it transparent until focus.
 */
export const TIME_PICKER_PLACEHOLDER = 'H:MM AM';

/**
 * The two names that exist only for assistive technology.
 *
 * Overridable, with English defaults, exactly as `DatePicker` handles `accessibleLabels`: the
 * wording is the same on almost every screen, so a default earns its place, and a caller in another
 * language still has somewhere to put theirs. Partial at the prop, so replacing one does not mean
 * restating the other.
 *
 * They are functions rather than strings because both interpolate the field's own `label` — a
 * screen reading two time fields side by side announces "Clear start time" and "Clear end time"
 * rather than two identical "Clear"s. A caller translating them writes the whole sentence rather
 * than being handed fragments to reassemble, which is the part word order differs on between
 * languages.
 *
 * The component itself stays free of `t()`, as every DS 2.0 component does: a design-system
 * primitive that reached for the app's i18n context could not be rendered outside it.
 */
export type TimePickerAccessibleLabels = {
  /**
   * The clear button's accessible name, given the field's `label`.
   *
   * REQUIRED BEHAVIOUR, not a nicety. The field this replaces renders its clear affordance as a
   * bare `IconButton` with no `aria-label`, so a screen reader announces it as "button" and nothing
   * else; that defect is pinned as a fact in the legacy suite's flag-off baseline precisely so this
   * field is not held to the same bar.
   */
  clear: (label: string) => string;
  /** The slot list's accessible name, given the field's `label`. */
  list: (label: string) => string;
};

/** Why a typed entry was refused: it is not a time, or it falls before `min`. */
export type TimePickerEntryError = 'format' | 'beforeMin';

/**
 * The sentences shown under the field when a typed entry is refused.
 *
 * Overridable with English defaults, for the reason `accessibleLabels` is, and shaped like
 * `DatePickerMessages` so one call site can translate both fields the same way. `limit` is the
 * earliest selectable time, written the way the field writes a time (`9:30 AM`).
 */
export type TimePickerMessages = {
  /** `hint` is the typed format (`H:MM AM`), `example` a real time written in it (`9:30 AM`). */
  format: (hint: string, example: string) => ReactNode;
  beforeMin: (limit: string) => ReactNode;
};

export const DEFAULT_TIME_PICKER_MESSAGES: TimePickerMessages = {
  format: (hint, example) => `Use the format ${hint}, like ${example}`,
  beforeMin: (limit) => `Pick ${limit} or a later time`,
};

export type TimePickerProps = {
  /**
   * The floating label. Required for the same reason as on `TextInput`, `TextArea` and
   * `DatePicker`: this shape has no other visible name, so a field without one is a nameless
   * control the moment the user types.
   */
  label: string;
  /**
   * The committed time, for a controlled field. `null` is an empty field, which is distinct from
   * `undefined` — `undefined` means "this field is uncontrolled", exactly as it does on an input.
   *
   * Only the TIME OF DAY is read. The date part is carried through untouched on every commit, so a
   * caller that keeps its times on a particular day keeps them there; see `TimePicker.tsx`.
   */
  value?: Date | null;
  /** The starting time for an uncontrolled field. */
  defaultValue?: Date | null;
  /**
   * Called with the committed time, or `null` when the field is cleared.
   *
   * Called from BOTH entry paths with the same value for the same time — a slot chosen from the
   * list and the same time typed into the field are indistinguishable here, and seconds and
   * milliseconds are zeroed either way so two equal times compare equal.
   *
   * NOT called for an entry that fails to parse or falls before `min`. Such an entry leaves the
   * committed value untouched and puts the field in its invalid state; see `TimePicker.tsx`.
   *
   * NOT called when the time has not changed either — blurring a field nobody typed into, or
   * re-typing the time that is already committed, reports nothing. Only a real change is a change.
   */
  onValueChange?: (value: Date | null) => void;
  /**
   * Called when the field starts refusing a typed entry (with the reason) and when it stops
   * (`null`), before any `onValueChange` the same edit causes, with the value the field holds once
   * it settles. A form that must not submit a refused entry can hold an invalid `Date` meanwhile:
   * passed back as `value`, the field reads one as "unchanged".
   */
  onEntryErrorChange?: (error: TimePickerEntryError | null, accepted: Date | null) => void;
  /**
   * Notified on every blur, before the typed entry is parsed.
   *
   * Separate from `onValueChange`, which reports a committed time and only when it actually moves.
   * A form library marking a field touched needs the blur itself, not the outcome of parsing it.
   */
  onBlur?: () => void;
  /**
   * The earliest selectable time, inclusive. Slots before it are ABSENT from the list rather than
   * disabled, and a typed entry before it is refused.
   *
   * Compared BY TIME OF DAY, not by instant — the calendar day of this `Date` is ignored entirely.
   * That is the one load-bearing divergence from `DatePicker`, it is not an approximation, and the
   * whole argument is in `slots.ts` beside the predicate that implements it. Read it before
   * changing anything here.
   */
  min?: Date;
  /**
   * Translations for the sentences shown when a typed entry is refused, merged over the English
   * defaults. A refused entry shows its own sentence in place of `helperText` until it is fixed.
   */
  messages?: Partial<TimePickerMessages>;
  /**
   * Sub-label text. Rendered as `Field.Error` when the field is invalid and `Field.Description`
   * otherwise, so a screen reader announces it either way via `aria-describedby`.
   */
  helperText?: ReactNode;
  /**
   * Turns the field red and marks the control `aria-invalid`.
   *
   * The caller's error and the component's own parse failure are ORed: a field the caller has not
   * flagged still goes red on an unparsable entry, and clearing the caller's flag does not clear a
   * parse failure the user has not fixed.
   */
  error?: boolean;
  disabled?: boolean;
  /**
   * Overrides for the two screen-reader-only names, merged over the English defaults.
   *
   * Not to be confused with `label`, which is the visible floating one. Nothing here is ever drawn.
   */
  accessibleLabels?: Partial<TimePickerAccessibleLabels>;
  /** Reaches the underlying `<input>`, so the field participates in an ordinary form submit. */
  name?: string;
  required?: boolean;
  /**
   * Where this field sits in a stacked group. Usually left unset and supplied by `FieldGroup`;
   * setting it explicitly overrides whatever the group would have assigned.
   */
  position?: TextInputPosition;
  /** Applied to the bordered box. */
  className?: string;
  /** Applied to the `Field.Root` wrapper rather than the box, for layout at the call site. */
  rootClassName?: string;
  /** Applied to the portalled slot-list popup. */
  popupClassName?: string;
  /** Controlled open state for the slot list. Usually left alone. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  'data-testid'?: string;
};
